import { ShippingLocation } from '@/types';
import { Search } from 'lucide-react';

interface LocationSelectorProps {
  locations: ShippingLocation[];
  selectedLocation: ShippingLocation | null;
  locationSearch: string;
  onLocationSearchChange: (value: string) => void;
  onLocationSelect: (location: ShippingLocation) => void;
  showLocationDropdown: boolean;
  onToggleLocationDropdown: () => void;
  selectedCountry: string;
  selectedState: string;
  stockPileEnabled?: boolean;
}

export function LocationSelector({
  locations,
  selectedLocation,
  locationSearch,
  onLocationSearchChange,
  onLocationSelect,
  showLocationDropdown,
  onToggleLocationDropdown,
  selectedCountry,
  selectedState,
  stockPileEnabled = false,
}: LocationSelectorProps) {
  // Filter locations based on selected country, state, and search term
  const filteredLocations = locations.filter(
    (location) =>
      location.country === selectedCountry &&
      location.state === selectedState &&
      location.location.toLowerCase().includes(locationSearch.toLowerCase())
  );

  // Add Stock-Pile and Store-Pickup options
  const specialOptions: ShippingLocation[] = [
    ...(stockPileEnabled ? [{ location: 'Stock-Pile', price: 0, country: '', state: '' }] : []),
    { location: 'Store-Pickup', price: 0, country: '', state: '' },
  ];

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Delivery Area
      </label>
      <button
        type="button"
        onClick={onToggleLocationDropdown}
        className="w-full h-12 px-4 text-left rounded-xl border border-gray-300 bg-white text-gray-900 hover:border-brand-400 focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={!selectedCountry || !selectedState}
      >
        {selectedLocation ? selectedLocation.location : 'Select Delivery Area'}
      </button>

      {showLocationDropdown && selectedCountry && selectedState && (
        <div className="absolute z-10 w-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200">
          <div className="p-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search locations..."
                value={locationSearch}
                onChange={(e) => onLocationSearchChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
            </div>
          </div>
          <div className="max-h-48 overflow-auto">
            {/* Render special options first */}
            {specialOptions.map((option) => (
              <button
                key={option.location}
                onClick={() => {
                  onLocationSelect(option);
                  onToggleLocationDropdown();
                }}
                className={`w-full px-4 py-2 text-left hover:bg-brand-50 transition-colors ${
                  selectedLocation?.location === option.location
                    ? 'bg-brand-50 text-brand-600 font-medium'
                    : 'text-gray-900'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span>{option.location}</span>
                  <span className="text-sm text-gray-500">
                    {option.price === 0 ? 'Free' : `+${option.price}`}
                  </span>
                </div>
              </button>
            ))}

            {/* Render filtered locations */}
            {filteredLocations.map((location) => (
              <button
                key={location.location}
                onClick={() => {
                  onLocationSelect(location);
                  onToggleLocationDropdown();
                }}
                className={`w-full px-4 py-2 text-left hover:bg-brand-50 transition-colors ${
                  selectedLocation?.location === location.location
                    ? 'bg-brand-50 text-brand-600 font-medium'
                    : 'text-gray-900'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span>{location.location}</span>
                  <span className="text-sm text-gray-500">
                    {location.price === 0 ? 'Free' : `+${location.price}`}
                  </span>
                </div>
              </button>
            ))}

            {/* Show message if no locations are found */}
            {filteredLocations.length === 0 && specialOptions.length === 0 && (
              <div className="px-4 py-2 text-gray-500 text-center">
                No locations found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
